import { useMutation } from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';

export interface E55ComparisonParams {
  formatType: 'html' | 'pdf' | 'xls';
  ppo?: string;
  inboundOrOutboundFilter?: string;
  fromQuarter?: number;
  toQuarter?: number;
  fromYear?: number;
  toYear?: number;
  samplingRelevant?: string[];
  notSamplingRelevant?: string[];
  selectedEno1File?: string;
}

interface E55DataDto {
  sendingOe: string;
  receivingOe: string;
  mailCategory: string;
  format: string;
  netOrGross: string;
  e55Weight: number | string | null;
}

interface Eno1DataDto {
  sendingOe: string;
  receivingOe: string;
  mailCategory: string;
  format: string;
  netOrGross: string;
  quarter: number;
  receptacleCategoryIncluded: boolean;
  receivedWeightStratum: number | string;
  stratum: string;
}

interface ComparisonResult {
  e55DataDto: E55DataDto | null;
  eno1DataDto: Eno1DataDto | null;
  difference: {
    kgDifference: string | null;
    percentageDifference: string | null;
  };
  year: number;
  quarter: number;
}

export interface MailItem {
  quarter: number;
  year: number;
  eno1Accepted: string | null;
  e55Accepted: string | null;
  eno1: string | null;
  e55: string | null;
  designatedOperator: string;
  compareResult: ComparisonResult[];
}

export interface OperatorData {
  items: MailItem[];
  operator: string;
}

export type MailDataStructure = MailItem[];

export interface E55ComparisonResponse {
  // For HTML format, contains the table data
  data?: MailDataStructure;
  url?: string;
  content?: Blob;
  filename?: string; // Add filename field for PDF/XLS downloads
}

export function useE55Comparison() {
  return useMutation<E55ComparisonResponse, Error, E55ComparisonParams>({
    mutationFn: async (params: E55ComparisonParams) => {
      // Create URLSearchParams to handle array parameters without brackets
      const searchParams = new URLSearchParams();
      
      // Add format type (required)
      searchParams.append('formatType', params.formatType);
      
      // Add optional parameters
      if (params.ppo) {
        searchParams.append('ppo', params.ppo);
      }
      if (params.inboundOrOutboundFilter) {
        searchParams.append('inboundOrOutboundFilter', params.inboundOrOutboundFilter);
      }
      if (params.fromQuarter) {
        searchParams.append('fromQuarter', params.fromQuarter.toString());
      }
      if (params.toQuarter) {
        searchParams.append('toQuarter', params.toQuarter.toString());
      }
      if (params.fromYear) {
        searchParams.append('fromYear', params.fromYear.toString());
      }
      if (params.toYear) {
        searchParams.append('toYear', params.toYear.toString());
      }
      if (params.selectedEno1File) {
        searchParams.append('selectedEno1File', params.selectedEno1File);
      }
      
      // Add sampling relevant array
      if (params.samplingRelevant && params.samplingRelevant.length > 0) {
        params.samplingRelevant.forEach(value => {
          searchParams.append('samplingRelevant', value);
        });
      }
      
      // Add not sampling relevant array  
      if (params.notSamplingRelevant && params.notSamplingRelevant.length > 0) {
        params.notSamplingRelevant.forEach(value => {
          searchParams.append('notSamplingRelevant', value);
        });
      }

      try {
        const response = await axios.get(`/reports/e55-comparison?${searchParams.toString()}`, {
          // HTML returns JSON with table data, PDF/XLS return binary files
          responseType: params.formatType === 'html' ? 'json' : 'blob',
        });

        // For HTML format, return the table data
        if (params.formatType === 'html') {
          return { data: response.data };
        }

        // For PDF/XLS, return the blob for file download
        let fileName: string | null = null;
        
        // Try to extract the filename from the Content-Disposition header
        const contentDisposition = 
          response.headers['content-disposition'] || 
          response.headers['Content-Disposition'];
        
        if (contentDisposition) {
          // Match the filename in Content-Disposition header
          const filenameMatch = /filename[^;=\n]*=(['"]?)([^"';]+)\1/i.exec(contentDisposition);
          if (filenameMatch && filenameMatch[2]) {
            fileName = decodeURIComponent(filenameMatch[2]);
          }
        }
        
        // If we couldn't get the filename from the header, generate one
        if (!fileName) {
          // Extract the current date in YYYYMMDD format for filename
          const date = new Date();
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const timestamp = String(date.getHours()).padStart(2, '0') + 
                            String(date.getMinutes()).padStart(2, '0') + 
                            String(date.getSeconds()).padStart(2, '0');
          
          // Format filename with timestamp
          fileName = `E55_Comparison_${year}${month}${day}_${timestamp}.${params.formatType}`;
        }
        
        const mimeType = params.formatType === 'pdf' 
          ? 'application/pdf' 
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        
        const blob = new Blob([response.data], { type: mimeType });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        return { 
          content: response.data,
          filename: fileName
        };
      } catch (error) {
        // Handle 204 No Content specifically
        if (axios.isAxiosError(error) && error.response?.status === 204) {
          throw new Error('No data available for the selected criteria. Please try different filters or date range.');
        }
        
        // Re-throw other errors as-is
        throw error;
      }
    },
  });
} 